import { Edit3, Receipt, Trash2, User } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import { CurrencyInput } from '@/shared/components/inputs';
import { DetailCard } from '@/shared/components/layout';
import {
  Badge,
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
  Textarea,
} from '@/shared/components/ui';
import { formatCurrencyFromCents } from '@/shared/utils/money';
import type {
  BillingPeriodStatus,
  ContractBillingPeriodFormData,
  ContractFormData,
} from '../types/contract-form';

type ContractBillingTabProps = {
  form: UseFormReturn<ContractFormData, any, ContractFormData>;
  fallbackBillingInterval?: ContractFormData['billingInterval'];
  readOnly?: boolean;
};

export function ContractBillingTab({
  form,
  fallbackBillingInterval,
  readOnly = false,
}: ContractBillingTabProps) {
  const { t, locale } = useIntl();
  const watchedBillingPeriods = useWatch({ control: form.control, name: 'billingPeriods' }) || [];

  const watchedBillingInterval = useWatch({
    control: form.control,
    name: 'billingInterval',
  });
  const normalizedBillingInterval =
    watchedBillingInterval === 'monthly' ||
    watchedBillingInterval === 'quarterly' ||
    watchedBillingInterval === 'yearly'
      ? watchedBillingInterval
      : fallbackBillingInterval || 'monthly';

  useEffect(() => {
    if (watchedBillingInterval !== normalizedBillingInterval) {
      form.setValue('billingInterval', normalizedBillingInterval, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [form, normalizedBillingInterval, watchedBillingInterval]);

  const openCents = watchedBillingPeriods
    .filter((p) => p.status !== 'paid')
    .reduce((sum, p) => sum + (p.amountCents || 0), 0);
  const overdueCents = watchedBillingPeriods
    .filter((p) => p.status !== 'paid' && p.dueDate)
    .filter((p) => DateTime.fromISO(p.dueDate as string) < DateTime.now())
    .reduce((sum, p) => sum + (p.amountCents || 0), 0);
  const planTotalCents = watchedBillingPeriods.reduce((sum, p) => sum + (p.amountCents || 0), 0);

  const [editingBillingPeriodIndex, setEditingBillingPeriodIndex] = useState<number | null>(null);
  const [editingBillingPeriodSnapshot, setEditingBillingPeriodSnapshot] =
    useState<ContractBillingPeriodFormData | null>(null);
  const canEdit = !readOnly;

  const openBillingPeriodEditor = (index: number) => {
    if (!canEdit) return;
    setEditingBillingPeriodSnapshot(form.getValues(`billingPeriods.${index}` as any));
    setEditingBillingPeriodIndex(index);
  };

  const closeBillingPeriodEditor = (opts: { restore: boolean }) => {
    if (opts.restore && editingBillingPeriodIndex !== null && editingBillingPeriodSnapshot) {
      form.setValue(
        `billingPeriods.${editingBillingPeriodIndex}` as any,
        editingBillingPeriodSnapshot,
        {
          shouldDirty: false,
        }
      );
    }
    setEditingBillingPeriodIndex(null);
    setEditingBillingPeriodSnapshot(null);
  };

  const removeBillingPeriod = (index: number) => {
    if (!canEdit) return;
    const current = form.getValues('billingPeriods') || [];
    const next = [...current.slice(0, index), ...current.slice(index + 1)];
    form.setValue('billingPeriods', next as any, { shouldDirty: true });
  };

  const isOverdueBillingPeriod = (period: ContractBillingPeriodFormData) => {
    if (!period.dueDate) return false;
    if (period.status === 'paid') return false;
    const due = DateTime.fromISO(period.dueDate as string);
    return due.isValid && due < DateTime.now();
  };

  const statusCounts = watchedBillingPeriods.reduce(
    (acc, period) => {
      const status = (period.status || 'scheduled') as BillingPeriodStatus;
      acc[status] = (acc[status] || 0) + 1;
      if (isOverdueBillingPeriod(period)) acc.overdue += 1;
      return acc;
    },
    {
      scheduled: 0,
      billable: 0,
      invoiced: 0,
      paid: 0,
      overdue: 0,
    } as Record<BillingPeriodStatus, number> & { overdue: number }
  );

  const getStatusBadgeClass = (status: BillingPeriodStatus, opts?: { overdue?: boolean }) => {
    if (opts?.overdue) {
      return 'bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/15';
    }

    switch (status) {
      case 'scheduled':
        return 'bg-secondary text-secondary-foreground border-transparent hover:bg-secondary/80';
      case 'billable':
        return 'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle hover:bg-warning-subtle/80';
      case 'invoiced':
        return 'bg-info-subtle text-info-subtle-foreground border-info-subtle hover:bg-info-subtle/80';
      case 'paid':
        return 'bg-success-subtle text-success-subtle-foreground border-success-subtle hover:bg-success-subtle/80';
      default:
        return 'bg-secondary text-secondary-foreground border-transparent hover:bg-secondary/80';
    }
  };

  const addBillingPeriod = () => {
    if (!canEdit) return;
    const start = form.getValues().startDate;
    const end = form.getValues().endDate;
    const paymentDays = Math.max(0, Math.trunc(form.getValues().paymentTermsDays ?? 30));
    const due = end ? DateTime.fromISO(end).plus({ days: paymentDays }).toISO() : undefined;

    const next = [
      ...(form.getValues().billingPeriods || []),
      {
        startDate: start || '',
        endDate: end || '',
        amountCents: 0,
        status: 'scheduled',
        dueDate: due || undefined,
        invoiceNumber: '',
        note: '',
      },
    ];
    form.setValue('billingPeriods', next as any, { shouldDirty: true });
  };

  return (
    <div className="grid gap-6">
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-9 space-y-6">
          <DetailCard
            icon={Receipt}
            title={t('contracts.billingPlan')}
            headerAction={
              canEdit ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="billing-add-period"
                  onClick={addBillingPeriod}
                >
                  {t('contracts.addBillingPeriod')}
                </Button>
              ) : undefined
            }
            noPadding
          >
            <div className="px-6 py-3">
              <div
                className="flex flex-wrap items-center gap-2"
                data-testid="billing-status-summary"
              >
                <Badge
                  variant="outline-solid"
                  className={getStatusBadgeClass('scheduled')}
                  data-testid="billing-status-chip-scheduled"
                >
                  {t('contracts.billingStatus.scheduled')} {statusCounts.scheduled}
                </Badge>
                <Badge
                  variant="outline-solid"
                  className={getStatusBadgeClass('billable')}
                  data-testid="billing-status-chip-billable"
                >
                  {t('contracts.billingStatus.billable')} {statusCounts.billable}
                </Badge>
                <Badge
                  variant="outline-solid"
                  className={getStatusBadgeClass('invoiced')}
                  data-testid="billing-status-chip-invoiced"
                >
                  {t('contracts.billingStatus.invoiced')} {statusCounts.invoiced}
                </Badge>
                <Badge
                  variant="outline-solid"
                  className={getStatusBadgeClass('paid')}
                  data-testid="billing-status-chip-paid"
                >
                  {t('contracts.billingStatus.paid')} {statusCounts.paid}
                </Badge>
                {statusCounts.overdue > 0 && (
                  <Badge
                    variant="outline-solid"
                    className={getStatusBadgeClass('scheduled', {
                      overdue: true,
                    })}
                    data-testid="billing-status-chip-overdue"
                  >
                    {t('contracts.billingStatus.overdue')} {statusCounts.overdue}
                  </Badge>
                )}
              </div>
            </div>

            <Table data-testid="billing-plan-table">
              <TableHeader>
                <TableHeaderRow>
                  <TableHead className="w-[240px] pl-6 py-2">
                    {t('contracts.billingPlanColumns.period')}
                  </TableHead>
                  <TableHead className="w-[140px] py-2">
                    {t('contracts.billingPlanColumns.dueDate')}
                  </TableHead>
                  <TableHead className="w-[140px] py-2">
                    {t('contracts.billingPlanColumns.amount')}
                  </TableHead>
                  <TableHead className="w-[140px] py-2">
                    {t('contracts.billingPlanColumns.status')}
                  </TableHead>
                  <TableHead className="w-[160px] py-2">
                    {t('contracts.billingPlanColumns.invoiceNumber')}
                  </TableHead>
                  <TableHead className="py-2">{t('contracts.billingPlanColumns.note')}</TableHead>
                  {canEdit && <TableHead className="w-[88px] pr-6 py-2" />}
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {watchedBillingPeriods.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canEdit ? 7 : 6}
                      className="text-sm text-muted-foreground pl-6 py-3"
                    >
                      {t('contracts.billingPlanEmpty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  watchedBillingPeriods.map((period, index) => (
                    <TableRow
                      key={`${period.id ?? index}`}
                      data-testid={`billing-period-row-${index}`}
                      className={`group ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                      onClick={() => {
                        if (!canEdit) return;
                        openBillingPeriodEditor(index);
                      }}
                    >
                      <TableCell className="pl-6 py-2">
                        {(() => {
                          const start = period?.startDate
                            ? DateTime.fromISO(period.startDate).toFormat('dd.MM.yyyy')
                            : '-';
                          const end = period?.endDate
                            ? DateTime.fromISO(period.endDate).toFormat('dd.MM.yyyy')
                            : '-';
                          return (
                            <div className="text-sm">
                              <div className="font-medium">{`${start} — ${end}`}</div>
                            </div>
                          );
                        })()}
                      </TableCell>

                      <TableCell className="py-2 text-muted-foreground">
                        {period?.dueDate
                          ? DateTime.fromISO(period.dueDate).toFormat('dd.MM.yyyy')
                          : '-'}
                      </TableCell>

                      <TableCell className="py-2">
                        {formatCurrencyFromCents(Number(period?.amountCents || 0), locale)}
                      </TableCell>

                      <TableCell className="py-2">
                        <Badge
                          variant="outline-solid"
                          className={getStatusBadgeClass(
                            (period?.status || 'scheduled') as BillingPeriodStatus,
                            { overdue: isOverdueBillingPeriod(period) }
                          )}
                        >
                          {t(
                            `contracts.billingStatus.${
                              (period?.status || 'scheduled') as BillingPeriodStatus
                            }` as any
                          )}
                        </Badge>
                      </TableCell>

                      <TableCell className="py-2">
                        {(() => {
                          const invoiceNumber = period?.invoiceNumber || '-';
                          return (
                            <span
                              className="block max-w-[140px] truncate text-muted-foreground"
                              title={invoiceNumber !== '-' ? invoiceNumber : undefined}
                            >
                              {invoiceNumber}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="py-2">
                        {(() => {
                          const note = period?.note || '-';
                          return (
                            <span
                              className="block max-w-[320px] truncate text-muted-foreground"
                              title={note !== '-' ? note : undefined}
                            >
                              {note}
                            </span>
                          );
                        })()}
                      </TableCell>

                      {canEdit && (
                        <TableCell className="text-right pr-6 py-2">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              data-testid={`billing-period-${index}-edit`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openBillingPeriodEditor(index);
                              }}
                              className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              type="button"
                              data-testid={`billing-period-${index}-remove`}
                              onClick={(event) => {
                                event.stopPropagation();
                                removeBillingPeriod(index);
                              }}
                              className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Dialog
              open={editingBillingPeriodIndex !== null}
              onOpenChange={(open) => {
                if (!open) closeBillingPeriodEditor({ restore: true });
              }}
            >
              <DialogContent className="sm:max-w-[720px]" data-testid="billing-period-edit-dialog">
                {editingBillingPeriodIndex !== null && (
                  <div className="space-y-6">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">{t('contracts.editBillingPeriod')}</div>
                      <div className="text-xs text-muted-foreground">
                        {(() => {
                          const p = watchedBillingPeriods[editingBillingPeriodIndex];
                          const start = p?.startDate
                            ? DateTime.fromISO(p.startDate).toFormat('dd.MM.yyyy')
                            : '-';
                          const end = p?.endDate
                            ? DateTime.fromISO(p.endDate).toFormat('dd.MM.yyyy')
                            : '-';
                          return `${start} — ${end}`;
                        })()}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`billingPeriods.${editingBillingPeriodIndex}.startDate` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('contracts.billingPlanColumns.startDate')}</FormLabel>
                            <FormControl>
                              <DatePicker
                                date={field.value ? new Date(field.value) : undefined}
                                disabled={!canEdit}
                                setDate={(date) => field.onChange(date?.toISOString() || '')}
                                placeholder={t('contracts.billingPlanChooseDate')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`billingPeriods.${editingBillingPeriodIndex}.endDate` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('contracts.billingPlanColumns.endDate')}</FormLabel>
                            <FormControl>
                              <DatePicker
                                date={field.value ? new Date(field.value) : undefined}
                                disabled={!canEdit}
                                setDate={(date) => field.onChange(date?.toISOString() || '')}
                                placeholder={t('contracts.billingPlanChooseDate')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`billingPeriods.${editingBillingPeriodIndex}.dueDate` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('contracts.billingPlanColumns.dueDate')}</FormLabel>
                            <FormControl>
                              <DatePicker
                                date={field.value ? new Date(field.value) : undefined}
                                disabled={!canEdit}
                                setDate={(date) => field.onChange(date?.toISOString() || undefined)}
                                placeholder={t('contracts.billingPlanChooseDate')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`billingPeriods.${editingBillingPeriodIndex}.amountCents` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('contracts.billingPlanColumns.amount')}</FormLabel>
                            <FormControl>
                              <CurrencyInput
                                testId="billing-period-edit-amount"
                                valueCents={Number(field.value || 0)}
                                onValueCentsChange={field.onChange}
                                disabled={!canEdit}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`billingPeriods.${editingBillingPeriodIndex}.status` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('contracts.billingPlanColumns.status')}</FormLabel>
                            <FormControl>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={!canEdit}
                              >
                                <SelectTrigger data-testid="billing-period-edit-status">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="scheduled">
                                    {t('contracts.billingStatus.scheduled')}
                                  </SelectItem>
                                  <SelectItem value="billable">
                                    {t('contracts.billingStatus.billable')}
                                  </SelectItem>
                                  <SelectItem value="invoiced">
                                    {t('contracts.billingStatus.invoiced')}
                                  </SelectItem>
                                  <SelectItem value="paid">
                                    {t('contracts.billingStatus.paid')}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`billingPeriods.${editingBillingPeriodIndex}.invoiceNumber` as any}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('contracts.billingPlanColumns.invoiceNumber')}</FormLabel>
                            <FormControl>
                              <Input
                                data-testid="billing-period-edit-invoiceNumber"
                                {...field}
                                disabled={!canEdit}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name={`billingPeriods.${editingBillingPeriodIndex}.note` as any}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('contracts.billingPlanColumns.note')}</FormLabel>
                          <FormControl>
                            <Textarea
                              data-testid="billing-period-edit-note"
                              {...field}
                              disabled={!canEdit}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => closeBillingPeriodEditor({ restore: true })}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => closeBillingPeriodEditor({ restore: false })}
                        disabled={!canEdit}
                      >
                        {t('common.apply')}
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <div className="px-6 py-4 border-t border-border bg-muted/60">
              <div className="grid grid-cols-3 gap-3" data-testid="billing-amount-summary">
                <div className="rounded-md border border-border bg-card p-3">
                  <div className="text-xs text-muted-foreground">
                    {t('contracts.billingSummary.total')}
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrencyFromCents(planTotalCents, locale)}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <div className="text-xs text-muted-foreground">
                    {t('contracts.billingSummary.open')}
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrencyFromCents(openCents, locale)}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <div className="text-xs text-muted-foreground">
                    {t('contracts.billingSummary.overdue')}
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrencyFromCents(overdueCents, locale)}
                  </div>
                </div>
              </div>
            </div>
          </DetailCard>
        </div>

        <div className="lg:col-span-3 space-y-6">
          <DetailCard icon={Receipt} title={t('contracts.billingSettings')}>
            <p className="text-sm text-muted-foreground mb-6">
              {t('contracts.billingSettingsDescription')}
            </p>

            <div className="space-y-6">
              <FormField
                control={form.control}
                name="billingTotalCents"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingTotal')}</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        testId="billing-total-input"
                        valueCents={Number(field.value || 0)}
                        onValueCentsChange={field.onChange}
                        disabled={!canEdit}
                      />
                    </FormControl>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatCurrencyFromCents(Number(field.value || 0), locale)}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingInterval"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingInterval')}</FormLabel>
                    <FormControl>
                      <Select
                        value={normalizedBillingInterval}
                        onValueChange={(value) => {
                          form.setValue(
                            'billingInterval',
                            value as ContractFormData['billingInterval'],
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            }
                          );
                          field.onChange(value);
                        }}
                        disabled={!canEdit}
                      >
                        <SelectTrigger data-testid="billing-interval-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">
                            {t('contracts.billingIntervalOptions.monthly')}
                          </SelectItem>
                          <SelectItem value="quarterly">
                            {t('contracts.billingIntervalOptions.quarterly')}
                          </SelectItem>
                          <SelectItem value="yearly">
                            {t('contracts.billingIntervalOptions.yearly')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="paymentTermsDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.paymentTermsDays')}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="payment-terms-input"
                        type="number"
                        value={String(field.value ?? 30)}
                        disabled={!canEdit}
                        onChange={(e) => field.onChange(Number.parseInt(e.target.value || '0', 10))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DetailCard>

          <DetailCard icon={User} title={t('contracts.billingContact')}>
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="billingPoNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingPoNumber')}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="billing-po-input"
                        value={field.value ?? ''}
                        disabled={!canEdit}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingContactName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingContactName')}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="billing-contact-name-input"
                        value={field.value ?? ''}
                        disabled={!canEdit}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingContactEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingContactEmail')}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="billing-contact-email-input"
                        type="email"
                        value={field.value ?? ''}
                        disabled={!canEdit}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DetailCard>
        </div>
      </div>
    </div>
  );
}
